<template>
  <div
    ref="iconWrapperRef"
    class="widget-info-icon-wrapper"
    @mouseenter="handleIconMouseEnter"
    @mouseleave="handleIconMouseLeave"
  >
    <slot name="trigger">
      <span class="material-icons-outlined info-icon">info</span>
    </slot>
    <Teleport
      v-if="tooltip"
      to="body"
    >
      <div
        v-if="showTooltip"
        ref="tooltipRef"
        class="widget-info-tooltip"
        :style="tooltipStyle"
        @mouseenter="handleTooltipMouseEnter"
        @mouseleave="handleTooltipMouseLeave"
      >
        {{ displayTooltip }}
        <a
          v-if="link"
          :href="link.url"
          target="_blank"
          rel="noopener noreferrer"
          class="widget-info-tooltip-link"
          @click="handleLinkClick"
        >
          {{ link.text }}
        </a>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted, inject } from 'vue'

const props = defineProps({
  /** Tooltip text. When provided, shows tooltip on hover. */
  tooltip: {
    type: String,
    default: ''
  },
  /** Optional link { url, text } shown in tooltip */
  link: {
    type: Object,
    default: null
  }
})

const trackPosthogEvent = inject('trackPosthogEvent', () => {})

const handleLinkClick = () => {
  trackPosthogEvent('Dashboard: Learn more clicked', {
    url: props.link?.url,
    text: props.link?.text
  })
}

const displayTooltip = computed(() => {
  const t = props.tooltip?.trim() || ''
  if (!t) return ''
  return /[.!?]$/.test(t) ? t : `${t}.`
})

const showTooltip = ref(false)
const iconWrapperRef = ref(null)
const tooltipRef = ref(null)
const tooltipPosition = ref(null)
let closeTimeout = null

const tooltipStyle = computed(() => {
  if (!tooltipPosition.value) return {}
  const { left, top, transform } = tooltipPosition.value
  return {
    left: `${left}px`,
    top: `${top}px`,
    transform: transform || 'translateX(-50%)'
  }
})

const updateTooltipPosition = () => {
  if (!iconWrapperRef.value || !tooltipRef.value) return
  const rect = iconWrapperRef.value.getBoundingClientRect()
  const tip = tooltipRef.value
  const tipWidth = tip.offsetWidth || 220
  const tipHeight = tip.offsetHeight || 60
  const gap = 2
  const padding = 12
  const viewportWidth = window.innerWidth
  let left = rect.left + rect.width / 2
  const minLeft = padding + tipWidth / 2
  const maxLeft = viewportWidth - padding - tipWidth / 2
  left = Math.max(minLeft, Math.min(maxLeft, left))
  tooltipPosition.value = {
    left,
    top: rect.top - tipHeight - gap,
    transform: 'translateX(-50%)'
  }
}

const handleIconMouseEnter = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout)
    closeTimeout = null
  }
  showTooltip.value = true
}

const handleIconMouseLeave = () => {
  if (props.tooltip) {
    closeTimeout = setTimeout(() => {
      showTooltip.value = false
      closeTimeout = null
    }, 150)
  } else {
    showTooltip.value = false
  }
}

const handleTooltipMouseEnter = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout)
    closeTimeout = null
  }
  showTooltip.value = true
}

const handleTooltipMouseLeave = () => {
  showTooltip.value = false
}

watch(showTooltip, (visible) => {
  if (visible && props.tooltip) {
    nextTick(() => {
      updateTooltipPosition()
      if (tooltipRef.value) requestAnimationFrame(updateTooltipPosition)
    })
  } else {
    tooltipPosition.value = null
  }
})

onUnmounted(() => {
  if (closeTimeout) clearTimeout(closeTimeout)
})
</script>

<style scoped>
.widget-info-icon-wrapper {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.widget-info-icon-wrapper .info-icon {
  font-size: 18px;
  color: rgba(0, 0, 0, 0.6);
  cursor: help;
  opacity: 0;
  transition: opacity 0.2s ease;
}

/* Show icon when directly hovering the icon (widget-level hover handled by BaseWidget) */
.widget-info-icon-wrapper:hover .info-icon {
  opacity: 1;
}

.widget-info-tooltip {
  position: fixed;
  background-color: #333;
  color: #fff;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  z-index: 10010;
  white-space: normal;
  min-width: 220px;
  max-width: 300px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  pointer-events: auto;
}

.widget-info-tooltip-link {
  display: block;
  width: fit-content;
  margin-top: 8px;
  padding: 6px 12px;
  color: #fff;
  background: transparent;
  border: 1px solid #fff;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  font-size: 12px;
  transition: background 0.2s ease, color 0.2s ease;
}

.widget-info-tooltip-link:hover {
  background: rgba(255, 255, 255, 0.15);
}
</style>
